import type { FirebaseApp } from 'firebase/app';
import { getAuth, getIdToken } from 'firebase/auth';
import { waitForClientAuthBootstrap } from './client-auth-bootstrap';

type GetCurrentUserIdTokenOptions = {
  forceRefresh?: boolean;
  requireUser?: boolean;
  timeoutMs?: number;
};

export class MissingCurrentUserIdTokenError extends Error {
  constructor() {
    super('No authenticated Firebase user is available for this request.');
    this.name = 'MissingCurrentUserIdTokenError';
  }
}
/**
 * Generate a Firebase ID token to communicate securely with
 * the MinervaAI API.
 *
 * @param Firebase app handle.
 * @returns A firebase ID token if the current user is valid,
 * or null if there is no logged in user.
 */
export const getCurrentUserIdToken = async (
  firebaseApp: FirebaseApp | undefined,
  options: GetCurrentUserIdTokenOptions = {}
) => {
  if (!firebaseApp) {
    throw new Error('No valid firebase app provided.');
  }

  const authHandle = getAuth(firebaseApp);
  let currentUser = authHandle.currentUser;

  /*
   * Once Firebase already has an in-memory user, protected API calls should
   * not sit behind the bootstrap barrier again. That barrier exists to prevent
   * empty-token requests during hard-refresh startup, but waiting here after a
   * user is present can add the full timeout to every client request in local
   * development when bootstrap bookkeeping lags behind the actual auth state.
   */
  if (!currentUser) {
    await waitForClientAuthBootstrap(options.timeoutMs);
    currentUser = authHandle.currentUser;
  }

  if (!currentUser) {
    if (options.requireUser) {
      throw new MissingCurrentUserIdTokenError();
    }

    return null;
  }

  const token = await getIdToken(currentUser, options.forceRefresh ?? false);

  return token;
};

export const requireCurrentUserIdToken = async (
  firebaseApp: FirebaseApp | undefined,
  options: Omit<GetCurrentUserIdTokenOptions, 'requireUser'> = {}
) => {
  return (await getCurrentUserIdToken(firebaseApp, {
    ...options,
    requireUser: true,
  })) as string;
};
